export interface ApiRequestOptions {
  signal?: AbortSignal;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

interface ErrorPayload {
  error?: {
    code?: string;
    message?: string;
  };
}

const request = async <TResponse, TBody = unknown>(
  method: 'GET' | 'POST',
  path: string,
  body?: TBody,
  options: ApiRequestOptions = {}
): Promise<TResponse> => {
  const url = `${BASE_URL}${path}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    signal: options.signal,
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    let errorBody: ErrorPayload | undefined;
    try {
      errorBody = (await response.json()) as ErrorPayload;
    } catch (error) {
      // ignore JSON parse issues
    }

    const message = errorBody?.error?.message ?? response.statusText ?? 'Request failed';
    throw new ApiError(message, response.status, errorBody?.error?.code);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  if (response.headers.get('content-type')?.includes('application/json')) {
    return (await response.json()) as TResponse;
  }

  return undefined as TResponse;
};

export const apiGet = <T>(path: string, options: ApiRequestOptions = {}) =>
  request<T>('GET', path, undefined, options);

export const apiPost = <TResponse, TBody = unknown>(
  path: string,
  body?: TBody,
  options: ApiRequestOptions = {}
) => request<TResponse, TBody>('POST', path, body, options);
